import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Home, LayoutDashboard, LogOut } from 'lucide-react';
import { api } from '@/api';
import { getStoredToken, getStoredUser, setStoredUser, initAuth, setAuthToken } from '@/utils/auth';

// Import split admin components
import { ResetPassword, AdminAuth, VerifyEmail, SetupPassword } from './AdminAuth';
import { TeamNameAutocomplete, MediaPickerDialog } from './AdminShared';
import { AdminDashboard } from './AdminDashboard';
import { AdminPartners, AdminPageBanners, AdminLeagues, AdminTeams } from './AdminSports';
import { AdminGames, AdminFixtures } from './AdminGamesSection';
import { AdminMediaLibrary } from './AdminContent';
import { AdminAdvertising, AdminSiteSettings } from './AdminAds';
import { AdminClubsManager } from './AdminClubs';
import { AdminUsers } from './AdminUsers';
import { MatchGraphicGenerator } from './MatchGraphicGenerator';
import { WeeklyScheduleGenerator } from './WeeklyScheduleGenerator';

const AdminPanel = ({ sports }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [stats, setStats] = useState({});
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('token') && location.pathname === '/verify') {
      return;
    }
  }, [location]);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getStoredToken();
      const user = getStoredUser();
      if (token && user) {
        initAuth();
        try {
          await api.getMe();
          setIsAuthenticated(true);
          setCurrentUser(user);
        } catch (error) {
          setAuthToken(null);
          setStoredUser(null);
        }
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, []);

  const handleLogin = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
  };

  const handleLogout = () => {
    setAuthToken(null);
    setStoredUser(null);
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  const loadAllData = useCallback(async () => {
    try {
      const [statsRes, leaguesRes, teamsRes, gamesRes, fixturesRes, partnersRes] = await Promise.all([
        api.getStats(),
        api.getLeagues(),
        api.getTeams(),
        api.getGames({ limit: 100 }),
        api.getFixtures({ limit: 100 }),
        api.getPartners(null)
      ]);
      setStats(statsRes.data);
      setLeagues(leaguesRes.data);
      setTeams(teamsRes.data);
      setGames(gamesRes.data);
      setFixtures(fixturesRes.data);
      setPartners(partnersRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated, loadAllData]);

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="animate-spin w-12 h-12 border-4 border-sky-400 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminAuth onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              <LayoutDashboard className="w-8 h-8 inline mr-3" /> Admin Panel
            </h1>
            <p className="text-gray-400">Welcome, {currentUser?.name}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-sky-400 text-sky-100" onClick={() => navigate('/')}>
              <Home className="w-4 h-4 mr-2" /> Back to Site
            </Button>
            <Button variant="outline" className="border-red-400 text-red-300 hover:bg-red-500/20" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap gap-1 bg-gray-800/50 mb-6 p-2">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Dashboard</TabsTrigger>
            <TabsTrigger value="leagues" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Conferences</TabsTrigger>
            <TabsTrigger value="teams" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Teams</TabsTrigger>
            <TabsTrigger value="games" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Games</TabsTrigger>
            <TabsTrigger value="fixtures" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Fixtures</TabsTrigger>
            <TabsTrigger value="graphics" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Graphics</TabsTrigger>
            <TabsTrigger value="clubs" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Clubs</TabsTrigger>
            <TabsTrigger value="partners" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Partners</TabsTrigger>
            <TabsTrigger value="banners" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Banners</TabsTrigger>
            <TabsTrigger value="media" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Media</TabsTrigger>
            <TabsTrigger value="advertising" className="data-[state=active]:bg-green-300 text-xs px-3 py-1.5">Advertising</TabsTrigger>
            <TabsTrigger value="admins" className="data-[state=active]:bg-amber-300 text-xs px-3 py-1.5" data-testid="admins-tab">Admins</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-sky-300 text-xs px-3 py-1.5">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <AdminDashboard stats={stats} sports={sports} leagues={leagues} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="leagues">
            <AdminLeagues leagues={leagues} sports={sports} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="teams">
            <AdminTeams teams={teams} leagues={leagues} sports={sports} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="games">
            <AdminGames games={games} teams={teams} leagues={leagues} sports={sports} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="fixtures">
            <AdminFixtures fixtures={fixtures} leagues={leagues} sports={sports} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="graphics">
            <Tabs defaultValue="match" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="match">Match Graphic</TabsTrigger>
                <TabsTrigger value="schedule">Weekly Schedule</TabsTrigger>
              </TabsList>
              <TabsContent value="match">
                <MatchGraphicGenerator games={games} leagues={leagues} />
              </TabsContent>
              <TabsContent value="schedule">
                <WeeklyScheduleGenerator />
              </TabsContent>
            </Tabs>
          </TabsContent>
          <TabsContent value="clubs">
            <AdminClubsManager leagues={leagues} />
          </TabsContent>
          <TabsContent value="partners">
            <AdminPartners partners={partners} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="banners">
            <AdminPageBanners onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="media">
            <AdminMediaLibrary />
          </TabsContent>
          <TabsContent value="advertising">
            <AdminAdvertising />
          </TabsContent>
          <TabsContent value="admins">
            <AdminUsers currentUser={currentUser} />
          </TabsContent>
          <TabsContent value="settings">
            <AdminSiteSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export { TeamNameAutocomplete, MediaPickerDialog, AdminPanel, AdminAuth, VerifyEmail, ResetPassword, SetupPassword };
